import { useCallback, useMemo, useState } from 'react';
import {
  DeliveryListItem,
  DeliveryStatus,
} from '@/services/api/deliveries';

/**
 * Bulk-selection rules from PRD §7.7.
 *
 * Each delivery falls into exactly one of four mutually-exclusive groups
 * (or none, when it isn't selectable). The user can only select rows
 * within a single group at a time.
 */
export type SelectionGroup =
  | 'QUEUEING'
  | 'IN_RETURN'
  | 'PRE_PICKUP'
  | 'POST_PICKUP';

/** Terminal / pre-pickup statuses where no bulk action is available. */
export const UNCHECKABLE_STATUSES: ReadonlySet<DeliveryStatus> = new Set<DeliveryStatus>([
  'PENDING_PAYMENT',
  'EXPIRED',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'RETURNED',
  'ON_HOLD',
  'FAILED_IN_RETURN',
  'DISPOSED',
  'PREPARING',
  'VERIFIED',
  'NOT_VERIFIED',
]);

/** Return which group an item belongs to, or `null` if it isn't selectable. */
export function groupOf(item: DeliveryListItem): SelectionGroup | null {
  if (UNCHECKABLE_STATUSES.has(item.status)) return null;
  if (item.provider?.id == null) return null;

  if (item.status === 'QUEUEING') return 'QUEUEING';
  if (item.status === 'IN_RETURN') return 'IN_RETURN';
  if (item.internalState === 'PRE_PICKUP') return 'PRE_PICKUP';
  if (item.internalState === 'POST_PICKUP') return 'POST_PICKUP';
  return null;
}

interface UseDeliverySelectionArgs {
  items: DeliveryListItem[];
  getKey?: (item: DeliveryListItem) => string;
}

export interface DeliverySelection {
  /** Currently selected delivery IDs. */
  selectedKeys: string[];
  /** The selected item objects, derived from `items` + `selectedKeys`. */
  selectedItems: DeliveryListItem[];
  /** Single active group derived from current selection, or null. */
  activeGroup: SelectionGroup | null;
  /** True if the current page would span multiple groups → block select-all. */
  pageSpansMultipleGroups: boolean;
  /** Predicate fed to TableData.selection.isRowSelectable. */
  isRowSelectable: (item: DeliveryListItem) => boolean;
  /** Setter for selectedKeys — usually wired straight to TableData. */
  setSelectedKeys: (keys: string[]) => void;
  /** Clears all selection. Call after a bulk action completes. */
  clear: () => void;
}

/**
 * Encapsulates the mutex selection rules so the list page stays focused
 * on rendering. Always pass the FULL list of items currently visible
 * on the page so `pageSpansMultipleGroups` is computed correctly.
 */
export function useDeliverySelection({
  items,
  getKey = (item) => item.deliveryID,
}: UseDeliverySelectionArgs): DeliverySelection {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const selectedItems = useMemo(() => {
    if (selectedKeys.length === 0) return [];
    const set = new Set(selectedKeys);
    return items.filter((item) => set.has(getKey(item)));
  }, [items, selectedKeys, getKey]);

  const activeGroup: SelectionGroup | null = useMemo(() => {
    if (selectedItems.length === 0) return null;
    const groups: SelectionGroup[] = [];
    for (const item of selectedItems) {
      const g = groupOf(item);
      if (g !== null && !groups.includes(g)) groups.push(g);
    }
    return groups.length === 1 ? groups[0] : null;
  }, [selectedItems]);

  const pageSpansMultipleGroups = useMemo(() => {
    const groups: SelectionGroup[] = [];
    for (const item of items) {
      const g = groupOf(item);
      if (g !== null && !groups.includes(g)) groups.push(g);
    }
    return groups.length > 1;
  }, [items]);

  const isRowSelectable = useCallback(
    (item: DeliveryListItem) => {
      const g = groupOf(item);
      if (g === null) return false;
      // While the user has at least one row selected, only same-group rows
      // can be added — that's the mutex rule from PRD §7.7.
      if (activeGroup !== null && g !== activeGroup) return false;
      return true;
    },
    [activeGroup]
  );

  const clear = useCallback(() => setSelectedKeys([]), []);

  return {
    selectedKeys,
    selectedItems,
    activeGroup,
    pageSpansMultipleGroups,
    isRowSelectable,
    setSelectedKeys,
    clear,
  };
}
